import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const AdSizeSettings = () => {
  const [adSizes, setAdSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const hasInitialized = React.useRef(false);

  const handleInitialize = async () => {
    // Prevent multiple simultaneous initialization calls
    if (initializing) {
      console.log('Already initializing, skipping duplicate call');
      return;
    }

    try {
      setInitializing(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/ad-sizes/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdSizes(response.data);
      return response.data;
    } catch (error) {
      // If already initialized, just fetch them
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes('already initialized')
      ) {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/ad-sizes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdSizes(response.data);
        return response.data;
      }
      toast.error(error.response?.data?.message || 'Failed to initialize content sizes');
      throw error;
    } finally {
      setInitializing(false);
    }
  };

  const fetchAdSizes = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/ad-sizes`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // If no ad sizes exist, automatically initialize them
      if (response.data.length === 0) {
        await handleInitialize();
      } else {
        setAdSizes(response.data);
      }
    } catch (error) {
      if (error.response?.status === 404 || error.response?.data?.message?.includes('not found')) {
        // Auto-initialize if endpoint returns 404
        await handleInitialize();
      } else {
        toast.error('Failed to load content sizes');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    fetchAdSizes();
  }, [fetchAdSizes]);

  const handleToggle = async (id, currentEnabled) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/ad-sizes/${id}`,
        { enabled: !currentEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAdSizes(adSizes.map(size => (size._id === id ? response.data : size)));
      toast.success(`Content size ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update Content size');
    }
  };

  const handleEdit = size => {
    setEditingId(size._id);
    setEditValues({
      name: size.name,
      displayName: size.displayName,
      decimalValue: size.decimalValue,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async id => {
    // Check if the name already exists in another entry
    const duplicateName = adSizes.find(
      size => size._id !== id && size.name.toLowerCase() === editValues.name.toLowerCase()
    );

    if (duplicateName) {
      toast.error('A content size with this name already exists. Please use a unique name.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/ad-sizes/${id}`,
        editValues,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAdSizes(adSizes.map(size => (size._id === id ? response.data : size)));
      toast.success('Content size updated successfully');
      setEditingId(null);
      setEditValues({});
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update content size');
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: field === 'decimalValue' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleCopy = async id => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/ad-sizes/${id}/copy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAdSizes([...adSizes, response.data]);
      toast.success('Content Size copied successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to copy content size');
    }
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this content size?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${process.env.REACT_APP_API_URL}/ad-sizes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setAdSizes(adSizes.filter(size => size._id !== id));
        toast.success('Content size deleted successfully');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete content size');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container px-4 py-8 mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Content Size Settings</h1>
      </div>

      {adSizes.length > 0 ? (
        <div className="overflow-hidden bg-white rounded-lg shadow">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                  Display Name
                </th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                  Decimal Value
                </th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                  Enabled
                </th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {adSizes.map(size => (
                <tr key={size._id}>
                  <td className="px-6 py-4 font-medium whitespace-nowrap">
                    {editingId === size._id ? (
                      <input
                        type="text"
                        value={editValues.name}
                        onChange={e => handleInputChange('name', e.target.value)}
                        className="px-2 py-1 w-full rounded border border-gray-300"
                      />
                    ) : (
                      size.name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === size._id ? (
                      <input
                        type="text"
                        value={editValues.displayName}
                        onChange={e => handleInputChange('displayName', e.target.value)}
                        className="px-2 py-1 w-full rounded border border-gray-300"
                      />
                    ) : (
                      size.displayName
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === size._id ? (
                      <input
                        type="number"
                        step="0.001"
                        value={editValues.decimalValue}
                        onChange={e => handleInputChange('decimalValue', e.target.value)}
                        className="px-2 py-1 w-full rounded border border-gray-300"
                      />
                    ) : (
                      size.decimalValue
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggle(size._id, size.enabled)}
                      className={`px-3 py-1 rounded text-sm ${
                        size.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {size.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === size._id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(size._id)}
                          className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="px-3 py-1 text-sm text-gray-700 bg-gray-300 rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCopy(size._id)}
                          className="px-3 py-1 text-sm text-purple-700 bg-purple-100 rounded hover:bg-purple-200"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => handleEdit(size)}
                          className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(size._id)}
                          className="px-3 py-1 text-sm text-red-700 bg-red-100 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-8 text-center text-gray-500">
          <p>Initializing default content sizes...</p>
        </div>
      )}
    </div>
  );
};

export default AdSizeSettings;
